import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { app_content_api_requests, public_app_content_api_requests, APP_CONTENT_API } from "../";
import { BaseDto, FilterGroup, EnumFilter } from "../../util/baseDto";
import { useNotification } from "../../../components/common/GlobalNotification";
import { useMemo } from "react";

const PATH = "/settings";
const BASE_QUERY_KEY = APP_CONTENT_API + PATH;

export type SettingType = "REGISTRATION" | "LOGIN";

export type SettingValueType = "BOOLEAN" | "STRING" | "INTEGER";

export interface Setting extends BaseDto {
  settingKey: string;
  description?: string;
  settingType: SettingType;
  value: string;
  valueType: SettingValueType;
}

export interface SettingsApiResponse {
  metaData: {
    size: number;
    totalPages: number;
    page: number;
    totalElements: number;
  };
  data: Setting[];
}

export interface CreateSettingRequest {
  settingKey: string;
  description?: string;
  settingType: SettingType;
  value: string;
  valueType: SettingValueType;
}

export interface UpdateSettingRequest extends Partial<CreateSettingRequest> {}

const useCreateSetting = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (request: CreateSettingRequest): Promise<Setting> =>
      app_content_api_requests.post(PATH, request),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Setting created successfully");
    },

    onError: () => {
      setErrorNotification("Failed to create setting");
    },
  });
};

const useUpdateSetting = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: number;
      request: UpdateSettingRequest;
    }): Promise<Setting> => app_content_api_requests.put(`${PATH}/${id}`, request),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Setting updated successfully");
    },

    onError: () => {
      setErrorNotification("Failed to update setting");
    },
  });
};

const useDeleteSetting = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (id: number): Promise<void> =>
      app_content_api_requests.delete(`${PATH}/${id}`),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Setting deleted successfully");
    },

    onError: () => {
      setErrorNotification("Failed to delete setting");
    },
  });
};

const useGetSettings = (size = 20, page = 0) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "all", page, size],
    queryFn: async (): Promise<SettingsApiResponse> =>
      public_app_content_api_requests.get(
        `${PATH}/public/all?page=${page}&size=${size}`
      ),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useSearchSettings = (
  size: number,
  page: number,
  settingType?: SettingType
) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [
        ...(settingType
          ? [EnumFilter("settingType", "EQUALS", settingType)]
          : []),
      ],
    }),
    [settingType]
  );

  return useInfiniteQuery<SettingsApiResponse>({
    queryKey: [BASE_QUERY_KEY, "search", settingType, size],
    queryFn: async ({ pageParam = page }) =>
      public_app_content_api_requests.search(
        `${PATH}/public/search?page=${pageParam}&size=${size}`,
        filterGroup
      ),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metaData.page ?? 0;
      const totalPages = lastPage.metaData.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetSettingById = (id?: number) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "by-id", id],
    queryFn: async (): Promise<Setting> =>
      app_content_api_requests.get(`${PATH}/${id}`),
    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export {
  BASE_QUERY_KEY as SETTINGS_QUERY_KEY,
  useCreateSetting,
  useUpdateSetting,
  useDeleteSetting,
  useGetSettings,
  useSearchSettings,
  useGetSettingById,
};
